import { IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import DateAndTimeRange from "@components/dates/DateAndTimeRange";
import TimeStamp from "@components/dates/TimeStamp";
import styles from "@components/InternalNote.module.css";
import Nbsp from "@components/Nbsp";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import {
  ConsultFragment as ConsultType,
  InternalNoteKindEnum,
  InternalNoteFragment as InternalNoteType
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  consult?: ConsultType;
  journeyId: string;
  note: InternalNoteType;
  onDelete?: (note: InternalNoteType) => void;
  onEdit?: (note: InternalNoteType) => void;
};

const InternalNote = ({ consult, journeyId, note, onDelete, onEdit }: Props) => {
  const myExpert = useMyExpert();
  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(note);
    }
  }, [note, onEdit]);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(note);
    }
  }, [note, onDelete]);

  const sanitizedBody = useSanitizeHtml(note.body, journeyId, note);

  return (
    <IonCard className={`${styles.card} ${styles.greyBackground}`}>
      <IonCardHeader>
        <IonCardTitle>
          <IonGrid className={styles.headerGrid}>
            <IonRow className="ion-align-items-center ion-justify-content-end">
              <IonCol>
                <IonText className={styles.headerText}>
                  <FormattedMessage id="models.internalNote.onlyVisibleToExperts" />
                </IonText>
              </IonCol>
              {note.kind === InternalNoteKindEnum.NOTE ? (
                <IonCol className="ion-margin-end" size="auto">
                  <>
                    <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={handleEdit} />
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      icon="trash"
                      onClick={handleDelete}
                    />
                  </>
                </IonCol>
              ) : onEdit ? (
                <IonCol className="ion-margin-end" size="auto">
                  <ActionIconButton ariaLabelKey="accessibility.ariaLabels.edit" icon="edit" onClick={handleEdit} />
                </IonCol>
              ) : null}
            </IonRow>
          </IonGrid>
        </IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        {note.kind === InternalNoteKindEnum.NOTE && (
          <>
            <IonText className={styles.expertName}>{note.expert.fullName}</IonText>
            <IonText className={styles.dropTime}>
              <TimeStamp value={note.createdAt} />
            </IonText>
            {note.consultReport && consult && (
              <div>
                <InternalLink
                  to={journeyRoute({
                    journeyId,
                    linkAfterJourney: `/consults/${consult.id}/consult-report`,
                    myExpert
                  })}
                >
                  <FormattedMessage id="models.consult.genericName" />
                  <Nbsp />
                  <DateAndTimeRange endTime={consult.endTime} startTime={consult.startTime} />
                </InternalLink>
              </div>
            )}
          </>
        )}

        <div className={styles.noteBody}>{sanitizedBody}</div>
      </IonCardContent>
    </IonCard>
  );
};

export default InternalNote;
